import * as React from 'react';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import { useLocation, useNavigate } from 'react-router-dom';
import { showGlobalModal } from 'core/components/GlobalModal';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import type {
  BranchRole,
  City,
  Class,
  Language,
} from 'core/utils/types/modules/org';
import { apiFileUpload } from 'core/utils/fileUpload/api';
import { useFetchBranchRole } from 'features/BranchSetup/hooks/useFetchBranchRole';
import { useFetchCity } from 'features/BranchSetup/hooks/useFetchCity';
import { useFetchClassList } from 'features/BranchSetup/hooks/useFetchClass';
import { useFetchLanguageList } from 'features/BranchSetup/hooks/useFetchLanguageList';
import httpclient from 'libs/Axios';
import { dmTextGenerationApi } from 'features/DigitalMarketing/api/dmTextGenerationApi';
import { DigitalMarketingBreadcrumb } from 'features/DigitalMarketing/components/DigitalMarketingBreadcrumb';
import { ImagePreviewModal } from 'features/DigitalMarketing/components/ImagePreviewModal';
import { DMFileUploadDropzone } from 'features/DigitalMarketing/components/DMFileUploadDropzone';
import { DMImagePreviewWithActions } from 'features/DigitalMarketing/components/DMImagePreviewWithActions';
import { DMAiDescriptionBox } from 'features/DigitalMarketing/components/DMAiDescriptionBox';
import { DMQualityHint } from 'features/DigitalMarketing/components/DMQualityHint';
import { useImageEnhancer } from 'features/DigitalMarketing/hooks/useImageEnhancer';
import { DMFormActions } from 'features/DigitalMarketing/components/DMFormActions';
import {
  DMDiv,
  DMInput,
  DMText,
  DMSelect,
  DMTextarea,
  DMOption,
} from 'features/DigitalMarketing/components/Primitives';
import { FieldLabel as SharedFieldLabel } from 'features/DigitalMarketing/components/FormControls';
import {
  buildEditWebsiteContentPath,
  EDIT_WEBSITE_CONTENT_BASE_PATH,
} from 'features/DigitalMarketing/routes/editWebsiteContentPaths';
import {
  EditWebsiteContentGenderField,
  EditWebsiteContentMobileField,
} from 'features/DigitalMarketing/components/EditWebsiteContentFields';
import {
  maxDescriptionGenerations,
  profileFormErrorClassName,
  profileFormInputClassName,
  profileFormLabelClassName,
} from 'features/DigitalMarketing/constants/formStyles';
import {
  extractUploadedUrl,
  getApiErrorMessage,
} from 'features/DigitalMarketing/utils/apiResponse';
import type { LeadershipProfile } from 'features/DigitalMarketing/pages/EditWebsiteContent/LeadershipProfiles/LeadershipProfileList';

const formatDateInput = (value?: string) => {
  if (!value) {
    return new Date().toISOString().slice(0, 10);
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value.slice(0, 10)
    : date.toISOString().slice(0, 10);
};

const defaultAbout =
  'Meet Kapil Sharma, the visionary founder of Bright Minds Academy. With a passion for education and a commitment to nurturing young talents, Kapil has dedicated his life to creating a supportive and innovative learning environment. His unique approach combines traditional values with modern teaching techniques, ensuring that every student thrives.';

type FormValues = {
  image: string;
  facultyName: string;
  middleName: string;
  lastName: string;
  staffCode: string;
  qualification: string;
  role: string;
  experience: string;
  email: string;
  gender: string;
  countryCode: string;
  mobile: string;
  about: string;
  languageId: number;
  cityId: number;
  pinCode: string;
  aadhaarNumber: string;
  birthDate: string;
  joiningDate: string;
  address: string;
  staffRoleIds: number[];
  classTeacherIds: number[];
};

type ProfileFormResource = 'profile' | 'staff' | 'faculty';

type ProfileFormApiConfig = {
  resource?: ProfileFormResource;
  create: string;
  edit: string;
  indexParam: string;
};

export type ProfileFormFlowConfig = {
  breadcrumbLabel: string;
  listSubPath: string;
  nameLabel: string;
  namePlaceholder: string;
  nameRequiredMessage: string;
  api: ProfileFormApiConfig;
};

export const leadershipProfileFormFlowConfig: ProfileFormFlowConfig = {
  breadcrumbLabel: 'Leadership Profiles',
  listSubPath: 'leadership-profile-list',
  nameLabel: 'Founder Name',
  namePlaceholder: 'Name of Founder',
  nameRequiredMessage: 'Founder name is required',
  api: {
    resource: 'profile',
    create: '/master/upsertFounder',
    edit: '/master/upsertFounder',
    indexParam: 'founder_id',
  },
};

const emptyValues: FormValues = {
  image: '',
  facultyName: '',
  middleName: '',
  lastName: '',
  staffCode: '',
  qualification: '',
  role: '',
  experience: '',
  email: '',
  gender: 'Male',
  countryCode: '+91',
  mobile: '',
  about: '',
  languageId: 0,
  cityId: 0,
  pinCode: '',
  aadhaarNumber: '',
  birthDate: formatDateInput(),
  joiningDate: formatDateInput(),
  address: '',
  staffRoleIds: [],
  classTeacherIds: [],
};

const editFallback: FormValues = {
  image: '',
  facultyName: 'Dr. Kapil Sharma',
  middleName: '',
  lastName: '',
  staffCode: '',
  qualification: 'PhD.',
  role: 'Founder',
  experience: '14',
  email: 'Example@Email.Com',
  gender: 'Male',
  countryCode: '+91',
  mobile: '8876543210',
  about: defaultAbout,
  languageId: 0,
  cityId: 0,
  pinCode: '',
  aadhaarNumber: '',
  birthDate: formatDateInput(),
  joiningDate: formatDateInput(),
  address: '',
  staffRoleIds: [],
  classTeacherIds: [],
};

const getValidationSchema = (config: ProfileFormFlowConfig) =>
  Yup.object({
    image: Yup.string(),
    facultyName: Yup.string().trim().required(config.nameRequiredMessage),
    qualification: Yup.string().trim().required('Qualification is required'),
    role:
      config.api.resource === 'faculty'
        ? Yup.string().trim()
        : Yup.string().trim().required('Role is required'),
    experience: Yup.string().trim().required('Experience is required'),
    email: Yup.string()
      .trim()
      .email('Enter a valid email')
      .required('Email is required'),
    gender: Yup.string().required('Gender is required'),
    countryCode: Yup.string().trim().required('Country code is required'),
    mobile: Yup.string()
      .trim()
      .matches(/^[0-9]{7,15}$/, 'Enter a valid mobile number')
      .required('Mobile number is required'),
    about: Yup.string().trim(),
    languageId:
      config.api.resource === 'staff'
        ? Yup.number()
            .min(1, 'Preferred language is required')
            .required('Preferred language is required')
        : Yup.number(),
    cityId:
      config.api.resource === 'staff'
        ? Yup.number().min(1, 'City is required').required('City is required')
        : Yup.number(),
    pinCode:
      config.api.resource === 'staff'
        ? Yup.string().trim().required('Pin code is required')
        : Yup.string(),
    birthDate:
      config.api.resource === 'staff'
        ? Yup.string().trim().required('Birth date is required')
        : Yup.string(),
    joiningDate:
      config.api.resource === 'staff'
        ? Yup.string().trim().required('Joining date is required')
        : Yup.string(),
    address:
      config.api.resource === 'staff'
        ? Yup.string().trim().required('Address is required')
        : Yup.string(),
    staffRoleIds:
      config.api.resource === 'staff'
        ? Yup.array().min(1, 'Select at least one faculty role')
        : Yup.array(),
  });

const inputClassName = profileFormInputClassName;
const labelClassName = profileFormLabelClassName;
const errorClassName = profileFormErrorClassName;
const normalizeRoleName = (roleName?: string) =>
  (roleName || '').toUpperCase().replace(/[^A-Z]/g, '');
const restrictedRoleNames = ['ADMIN', 'SUPERADMIN'];
const normalizeStaffGender = (gender: string | number | undefined) => {
  if (gender === 0) {
    return 'MALE';
  }

  if (gender === 1) {
    return 'FEMALE';
  }

  if (gender === 2) {
    return 'OTHER';
  }

  const normalizedGender = String(gender || '').toUpperCase();
  return ['MALE', 'FEMALE', 'OTHER', 'NA'].includes(normalizedGender)
    ? normalizedGender
    : 'NA';
};

const normalizeProfileGender = (gender: string | undefined) => {
  const normalizedGender = String(gender || '').toUpperCase();

  return ['MALE', 'FEMALE', 'OTHER', 'NA'].includes(normalizedGender)
    ? normalizedGender
    : 'NA';
};

const toGenderInputValue = (gender: string | undefined) => {
  const normalizedGender = normalizeProfileGender(gender);

  if (normalizedGender === 'NA') {
    return '';
  }

  return normalizedGender.charAt(0) + normalizedGender.slice(1).toLowerCase();
};

const FieldError = ({
  formik,
  name,
}: {
  formik: FormikProps<FormValues>;
  name: keyof FormValues;
}) => {
  const error = formik.touched[name] && formik.errors[name];

  return error ? <DMText className={errorClassName}>{error}</DMText> : null;
};

const FieldLabel = ({
  children,
  required = false,
}: {
  children: React.ReactNode;
  required?: boolean;
}) => (
  <SharedFieldLabel className={labelClassName} required={required}>
    {children}
  </SharedFieldLabel>
);

const FacultyProfileRoleDefault = ({
  defaultRole,
  formik,
  shouldApply,
}: {
  defaultRole?: BranchRole;
  formik: FormikProps<FormValues>;
  shouldApply: boolean;
}) => {
  React.useEffect(() => {
    if (!shouldApply || !defaultRole?.BRANCH_ROLE_ID || !defaultRole?.NAME) {
      return;
    }

    if (
      formik.values.role === defaultRole.NAME &&
      formik.values.staffRoleIds.length === 1 &&
      formik.values.staffRoleIds[0] === defaultRole.BRANCH_ROLE_ID
    ) {
      return;
    }

    formik.setFieldValue('role', defaultRole.NAME);
    formik.setFieldValue('staffRoleIds', [defaultRole.BRANCH_ROLE_ID]);
  }, [defaultRole, formik, shouldApply]);

  return null;
};

type LeadershipProfileFormProps = {
  config?: ProfileFormFlowConfig;
};

export function LeadershipProfileForm({
  config = leadershipProfileFormFlowConfig,
}: LeadershipProfileFormProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const authData = useAuth();
  const isEditMode = location.pathname.endsWith('/edit');
  const profile = location.state?.profile as LeadershipProfile | undefined;
  const [branchRoleList, setBranchRoleList] = React.useState<BranchRole[]>([]);
  const [cityList, setCityList] = React.useState<City[]>([]);
  const [classList, setClassList] = React.useState<Class[]>([]);
  const [languageList, setLanguageList] = React.useState<Language[]>([]);
  const [staffDetails, setStaffDetails] = React.useState<any>();
  const [isUploadingImage, setIsUploadingImage] = React.useState(false);
  const [isGeneratingDescription, setIsGeneratingDescription] =
    React.useState(false);
  const [previewImageUrl, setPreviewImageUrl] = React.useState('');
  const { enhanceImage, isEnhancingImage } = useImageEnhancer();
  const [descriptionGenerationCount, setDescriptionGenerationCount] =
    React.useState(0);
  const isStaffResource = config.api.resource === 'staff';
  const isFacultyResource = config.api.resource === 'faculty';
  const canShowDescription = true;
  const descriptionGenerationsRemaining =
    maxDescriptionGenerations - descriptionGenerationCount;
  const isCurrentUserSuperAdmin =
    normalizeRoleName(authData?.STAFF_ROLE?.STAFF_ROLE_NAME) === 'SUPERADMIN';
  const canShowRole = React.useCallback(
    (roleName?: string) =>
      isCurrentUserSuperAdmin ||
      !restrictedRoleNames.includes(normalizeRoleName(roleName)),
    [isCurrentUserSuperAdmin]
  );

  useFetchBranchRole(branchRoleList, setBranchRoleList);
  useFetchCity(cityList, setCityList);
  useFetchClassList(classList, setClassList, !isStaffResource);
  useFetchLanguageList(languageList, setLanguageList);

  const roleOptions = React.useMemo(
    () =>
      (branchRoleList || [])
        .filter(role => role?.NAME && canShowRole(role.NAME))
        .map(role => role.NAME),
    [branchRoleList, canShowRole]
  );

  const staffRoleOptions = React.useMemo(
    () =>
      (branchRoleList || []).filter(
        role => role?.BRANCH_ROLE_ID && role?.NAME && canShowRole(role.NAME)
      ),
    [branchRoleList, canShowRole]
  );

  const defaultFacultyProfileRole = React.useMemo(
    () =>
      staffRoleOptions.find(
        role => normalizeRoleName(role.NAME) === 'FACULTY'
      ) ||
      staffRoleOptions.find(role => normalizeRoleName(role.NAME) === 'TEACHER'),
    [staffRoleOptions]
  );

  const shouldLockRoleSelect = isStaffResource && !isEditMode;

  React.useEffect(() => {
    if (!isStaffResource || !isEditMode || !profile?.staffId) {
      return;
    }

    const requestStaffDetails = async () => {
      try {
        const response = await httpclient().get(
          `/data/staff/view?id=${profile.staffId}&academic_year_id=${authData?.SELECTED_ACADEMIC_YEAR?.ACADEMIC_YEAR_ID}`
        );
        setStaffDetails(response?.data?.data || profile.rawStaff);
      } catch (error: any) {
        showGlobalModal(
          getApiErrorMessage(error, 'Unable to fetch staff details'),
          'error'
        );
        setStaffDetails(profile.rawStaff);
      }
    };

    requestStaffDetails();
  }, [
    authData?.SELECTED_ACADEMIC_YEAR?.ACADEMIC_YEAR_ID,
    isEditMode,
    isStaffResource,
    profile,
  ]);

  const initialValues = React.useMemo<FormValues>(() => {
    if (!isEditMode) {
      return emptyValues;
    }

    if (!profile) {
      return editFallback;
    }

    const rawStaff = staffDetails || profile.rawStaff || {};

    return {
      image: profile.image || '',
      facultyName:
        rawStaff?.first_name || rawStaff?.FIRST_NAME || profile.facultyName,
      middleName: rawStaff?.middle_name || rawStaff?.MIDDLE_NAME || '',
      lastName: rawStaff?.last_name || rawStaff?.LAST_NAME || '',
      staffCode: rawStaff?.STAFF_CODE || '',
      qualification: profile.qualification,
      role:
        rawStaff?.STAFF_ROLES?.[0]?.BranchRole?.NAME ||
        rawStaff?.STAFF_ROLES?.[0]?.BRANCH_ROLE_NAME ||
        profile.role,
      experience: profile.experience,
      email: profile.email,
      gender: toGenderInputValue(profile.gender),
      countryCode: profile.countryCode || '+91',
      mobile: profile.mobile,
      about: profile.about,
      languageId: rawStaff?.LANGUAGE_ID || 0,
      cityId: rawStaff?.CITY_ID || 0,
      pinCode: rawStaff?.PINCODE ? String(rawStaff.PINCODE) : '',
      aadhaarNumber: rawStaff?.AADHAR_NUMBER || '',
      birthDate: formatDateInput(rawStaff?.BIRTH_DATE),
      joiningDate: formatDateInput(rawStaff?.JOINING_DATE),
      address: rawStaff?.ADDRESS || profile.about || '',
      staffRoleIds:
        rawStaff?.STAFF_ROLES?.map(
          (role: any) =>
            role?.BranchRole?.BRANCH_ROLE_ID || role?.BRANCH_ROLE_ID
        )?.filter(Boolean) || [],
      classTeacherIds:
        rawStaff?.CLASS_TEACHER?.map(
          (item: any) => item?.Class?.CLASS_ID || item?.CLASS_ID
        )?.filter(Boolean) || [],
    };
  }, [isEditMode, profile, staffDetails]);

  const getProfilePayload = (values: FormValues) => ({
    founder_id: isEditMode ? profile?.founderId : undefined,
    founder_name: values.facultyName,
    role: values.role,
    qualification: values.qualification,
    experience: values.experience,
    gender: normalizeProfileGender(values.gender),
    email_id: values.email,
    description: values.about || '-',
    image_url: values.image,
    country_code: values.countryCode,
    mobile_number: values.mobile,
  });

  const getStaffPayload = (values: FormValues) => [
    {
      STAFF_ID: isEditMode ? profile?.staffId : undefined,
      FIRST_NAME: values.facultyName,
      LAST_NAME: values.lastName || undefined,
      STAFF_CODE: values.staffCode || undefined,
      GENDER: normalizeStaffGender(values.gender),
      LANGUAGE_ID: Number(values.languageId) || undefined,
      PHOTO_URL: values.image,
      QUALIFICATION: values.qualification,
      TEACHING_SINCE: values.experience,
      COUNTRY_CODE: values.countryCode,
      MOBILE_NUMBER: values.mobile,
      DESCRIPTION: values.about || undefined,
      ADDRESS: values.address || values.about || undefined,
      CITY_ID: Number(values.cityId) || undefined,
      PINCODE: String(values.pinCode || ''),
      EMAIL_ID: values.email,
      AADHAR_NUMBER: values.aadhaarNumber || undefined,
      BIRTH_DATE: values.birthDate,
      JOINING_DATE: values.joiningDate,
      BRANCH_ID: authData?.BRANCH_ID,
      INSTITUTE_ID: authData?.INSTITUTE_ID,
      CLASS_TEACHER: values.classTeacherIds.map(classId => ({
        CLASS_ID: Number(classId),
      })),
      STAFF_ROLES: values.staffRoleIds.map(roleId => ({
        BRANCH_ROLE_ID: Number(roleId),
      })),
    },
  ];

  const getFacultyPayload = (values: FormValues) => ({
    staff_id: isEditMode ? profile?.staffId : undefined,
    first_name: values.facultyName,
    last_name: values.lastName,
    middle_name: values.middleName,
    gender: normalizeProfileGender(values.gender),
    qualification: values.qualification,
    teaching_since: Number(values.experience) || values.experience,
    photo_url: values.image,
    email_id: values.email,
    country_code: values.countryCode,
    mobile_number: values.mobile,
    description: values.about || undefined,
  });

  const handleUploadProfilePicture = async (
    file: File,
    formik: FormikProps<FormValues>
  ) => {
    if (!file.type.startsWith('image/')) {
      showGlobalModal('Only image files are allowed', 'error');
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      showGlobalModal('File size exceeds 15MB', 'error');
      return;
    }

    const localPreviewUrl = URL.createObjectURL(file);
    formik.setFieldValue('image', localPreviewUrl);
    formik.setFieldTouched('image', true, false);
    setIsUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append('dest', 'WEBSITE');
      formData.append('branch_id', authData?.BRANCH_ID as any);
      formData.append('database', authData?.DATABASE as any);
      formData.append('files', file);

      const s3Url = await apiFileUpload(
        formData,
        'upload',
        '',
        '',
        true,
        false
      );
      const finalUrl = extractUploadedUrl(s3Url, isEditMode);

      if (finalUrl) {
        formik.setFieldValue('image', finalUrl);
        formik.setFieldTouched('image', true, false);
      } else {
        if (formik.values.image === localPreviewUrl) {
          formik.setFieldValue('image', '');
        }
        showGlobalModal('Unable to read uploaded image URL', 'error');
      }
    } catch (error: any) {
      if (formik.values.image === localPreviewUrl) {
        formik.setFieldValue('image', '');
      }
      showGlobalModal(
        getApiErrorMessage(error, 'Failed to upload image. Please try again.'),
        'error'
      );
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleGenerateDescription = async (formik: FormikProps<FormValues>) => {
    if (descriptionGenerationsRemaining <= 0) {
      showGlobalModal('Description generation limit reached.', 'warning');
      return;
    }

    const missingFields = [
      'facultyName',
      ...(isFacultyResource ? [] : ['role']),
      'qualification',
      'experience',
    ].filter(field => !formik.values[field as keyof FormValues]);

    if (missingFields.length) {
      formik.setTouched({
        ...formik.touched,
        facultyName: true,
        role: !isFacultyResource,
        qualification: true,
        experience: true,
      });
      showGlobalModal(
        'Complete the required profile details before generating a description.',
        'warning'
      );
      return;
    }

    setIsGeneratingDescription(true);

    try {
      const description = await dmTextGenerationApi.generateDescription({
        type: 'PROFILE',
        name: formik.values.facultyName,
        role: formik.values.role || (isFacultyResource ? 'Faculty' : ''),
        qualification: formik.values.qualification,
        experience: formik.values.experience,
        gender: formik.values.gender,
      });

      if (description) {
        formik.setFieldValue('about', description);
        formik.setFieldTouched('about', true, false);
        setDescriptionGenerationCount(current =>
          Math.min(current + 1, maxDescriptionGenerations)
        );
      } else {
        showGlobalModal('Unable to generate description', 'error');
      }
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to generate description'),
        'error'
      );
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const saveProfile = async (
    values: FormValues,
    formikHelpers?: FormikHelpers<FormValues>
  ) => {
    formikHelpers?.setSubmitting(true);

    try {
      const payload = isStaffResource
        ? getStaffPayload(values)
        : isFacultyResource
        ? getFacultyPayload(values)
        : getProfilePayload(values);
      const response = isStaffResource
        ? await httpclient().post(config.api.create, payload)
        : await httpclient().post(config.api.create, payload);

      showGlobalModal(
        response?.data?.message ||
          (isEditMode
            ? 'Profile updated successfully'
            : 'Profile created successfully')
      );
      navigate(buildEditWebsiteContentPath(config.listSubPath));
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to save profile'),
        'error'
      );
    } finally {
      formikHelpers?.setSubmitting(false);
    }
  };

  const handleSubmit = async (
    values: FormValues,
    formikHelpers: FormikHelpers<FormValues>
  ) => {
    if (isEditMode && profile?.status === 'Publish') {
      formikHelpers.setSubmitting(false);
      showGlobalModal(
        <>
          <DMText as="span" className="block font-manropeB text-[2.6vh]">
            All changes made will be published on the school website
          </DMText>
          <DMText
            as="span"
            className="mt-[1vh] block text-[1.8vh] text-gray-500"
          >
            Do you want to make changes?
          </DMText>
        </>,
        'warning',
        {
          showConfirm: true,
          onConfirm: () => saveProfile(values, formikHelpers),
          onCancel: true,
          confimLabel: 'Yes',
          cancelLabel: 'Cancel',
        }
      );
      return;
    }

    await saveProfile(values, formikHelpers);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={getValidationSchema(config)}
      onSubmit={handleSubmit}
    >
      {(formik: FormikProps<FormValues>) => (
        <DMDiv
          as="main"
          className="flex h-full min-h-0 flex-col overflow-hidden bg-white px-[2.5vw] py-[2.2vh] font-inter text-gray-950"
        >
          {isStaffResource && (
            <FacultyProfileRoleDefault
              defaultRole={defaultFacultyProfileRole}
              formik={formik}
              shouldApply={shouldLockRoleSelect}
            />
          )}

          <DigitalMarketingBreadcrumb
            className="mb-[4vh] shrink-0"
            items={[
              {
                label: 'Edit Website Content',
                path: EDIT_WEBSITE_CONTENT_BASE_PATH,
              },
              {
                label: config.breadcrumbLabel,
                path: buildEditWebsiteContentPath(config.listSubPath),
              },
              { label: isEditMode ? 'Edit Profile' : 'Create Profile' },
            ]}
          />

          <Form
            className={`grid min-h-0 flex-1 auto-rows-max grid-cols-[1.25fr_1fr] content-start ${
              isFacultyResource
                ? 'gap-x-[2vw] gap-y-[2.2vh]'
                : 'gap-x-[1.7vw] gap-y-[1.6vh]'
            } overflow-x-hidden overflow-y-auto`}
          >
            <DMDiv>
              <FieldLabel>Profile Picture</FieldLabel>
              {formik.values.image ? (
                <DMImagePreviewWithActions
                  className={`${
                    isFacultyResource
                      ? 'h-[27vh] gap-[2vw]'
                      : isStaffResource
                      ? 'h-[22vh] gap-[2vw]'
                      : 'h-[31vh] gap-[3.5vw]'
                  }`}
                  imageSize={`${
                    isFacultyResource
                      ? 'h-[22vh] w-[22vh]'
                      : isStaffResource
                      ? 'h-[18vh] w-[18vh]'
                      : 'h-[25.5vh] w-[25.5vh]'
                  }`}
                  buttonWidth="w-[16.5vw]"
                  imageUrl={formik.values.image}
                  altText={formik.values.facultyName || 'Profile'}
                  isUploading={isUploadingImage}
                  isEnhancing={isEnhancingImage}
                  onPreview={() => setPreviewImageUrl(formik.values.image)}
                  onUpload={file => handleUploadProfilePicture(file, formik)}
                  onEnhance={async () => {
                    const enhancedUrl = await enhanceImage(formik.values.image);
                    if (enhancedUrl) formik.setFieldValue('image', enhancedUrl);
                  }}
                  onRemove={() => {
                    formik.setFieldValue('image', '');
                    formik.setFieldTouched('image', true, false);
                  }}
                />
              ) : (
                <DMFileUploadDropzone
                  className={`${
                    isFacultyResource
                      ? 'h-[27vh]'
                      : isStaffResource
                      ? 'h-[22vh]'
                      : 'h-[31vh]'
                  }`}
                  buttonClassName={`mt-[1.4vh] flex h-[5.9vh] w-[16.5vw] items-center justify-center gap-[0.8vw] rounded-full border border-primary-main bg-white text-[1.85vh] font-semibold text-primary-main ${
                    isUploadingImage
                      ? 'cursor-not-allowed opacity-60'
                      : 'cursor-pointer'
                  }`}
                  isUploading={isUploadingImage}
                  onFileSelect={file =>
                    handleUploadProfilePicture(file, formik)
                  }
                />
              )}
              <FieldError formik={formik} name="image" />
              {!isEditMode && <DMQualityHint className="mt-[1vh]" />}
            </DMDiv>

            {canShowDescription && (
              <DMDiv>
                <DMAiDescriptionBox
                  value={formik.values.about}
                  onChange={formik.handleChange}
                  onGenerate={() => handleGenerateDescription(formik)}
                  isGenerating={isGeneratingDescription}
                  generationsRemaining={descriptionGenerationsRemaining}
                  className={`${
                    isFacultyResource
                      ? 'h-[27vh] px-[1.1vw] py-[1.1vh]'
                      : isStaffResource
                      ? 'h-[22vh] px-[1.1vw] py-[1.1vh]'
                      : 'h-[31vh] px-[1.25vw] py-[1.6vh]'
                  }`}
                  labelClassName={labelClassName}
                />
                <FieldError formik={formik} name="about" />
              </DMDiv>
            )}

            <DMDiv
              className={`grid auto-rows-max ${
                isFacultyResource
                  ? 'col-span-2 grid-cols-3 gap-x-[2vw] gap-y-[1.8vh]'
                  : 'grid-cols-2 gap-x-[1.5vw] gap-y-[1.2vh]'
              }`}
            >
              <DMDiv
                className={
                  config.api.resource === 'staff' ||
                  config.api.resource === 'faculty'
                    ? ''
                    : 'col-span-2'
                }
              >
                <FieldLabel required>{config.nameLabel}</FieldLabel>
                <DMInput
                  name="facultyName"
                  value={formik.values.facultyName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={config.namePlaceholder}
                  className={inputClassName}
                />
                <FieldError formik={formik} name="facultyName" />
              </DMDiv>
              {config.api.resource === 'faculty' && (
                <DMDiv>
                  <FieldLabel>Middle Name</FieldLabel>
                  <DMInput
                    name="middleName"
                    value={formik.values.middleName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter Middle Name"
                    className={inputClassName}
                  />
                  <FieldError formik={formik} name="middleName" />
                </DMDiv>
              )}
              {(config.api.resource === 'staff' ||
                config.api.resource === 'faculty') && (
                <DMDiv>
                  <FieldLabel>Last Name</FieldLabel>
                  <DMInput
                    name="lastName"
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter Last Name"
                    className={inputClassName}
                  />
                  <FieldError formik={formik} name="lastName" />
                </DMDiv>
              )}
              {config.api.resource === 'staff' && (
                <DMDiv>
                  <FieldLabel>Faculty Code</FieldLabel>
                  <DMInput
                    name="staffCode"
                    value={formik.values.staffCode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter Faculty Code"
                    className={inputClassName}
                  />
                  <FieldError formik={formik} name="staffCode" />
                </DMDiv>
              )}
              <DMDiv
                className={
                  config.api.resource === 'staff'
                    ? ''
                    : config.api.resource === 'faculty'
                    ? ''
                    : 'col-span-2'
                }
              >
                <FieldLabel required>Qualification</FieldLabel>
                <DMInput
                  name="qualification"
                  value={formik.values.qualification}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter Qualification"
                  className={inputClassName}
                />
                <FieldError formik={formik} name="qualification" />
              </DMDiv>
              {!shouldLockRoleSelect && !isFacultyResource && (
                <DMDiv>
                  <FieldLabel required>Role</FieldLabel>
                  {config.api.resource === 'profile' ? (
                    <DMInput
                      name="role"
                      value={formik.values.role}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Enter Role"
                      className={inputClassName}
                    />
                  ) : (
                    <DMSelect
                      name="role"
                      value={formik.values.role}
                      onChange={event => {
                        const selectedRoleName = event.target.value;
                        formik.setFieldValue('role', selectedRoleName);

                        if (config.api.resource === 'staff') {
                          const selectedRole = branchRoleList.find(
                            role => role.NAME === selectedRoleName
                          );
                          formik.setFieldValue(
                            'staffRoleIds',
                            selectedRole?.BRANCH_ROLE_ID
                              ? [selectedRole.BRANCH_ROLE_ID]
                              : []
                          );
                        }
                      }}
                      onBlur={formik.handleBlur}
                      className={inputClassName}
                    >
                      <DMOption value="">Select Role</DMOption>
                      {formik.values.role &&
                        !roleOptions.includes(formik.values.role) &&
                        canShowRole(formik.values.role) && (
                          <DMOption value={formik.values.role}>
                            {formik.values.role}
                          </DMOption>
                        )}
                      {roleOptions.map(roleName => (
                        <DMOption key={roleName} value={roleName}>
                          {roleName}
                        </DMOption>
                      ))}
                    </DMSelect>
                  )}
                  <FieldError formik={formik} name="role" />
                  {config.api.resource === 'staff' && (
                    <FieldError formik={formik} name="staffRoleIds" />
                  )}
                </DMDiv>
              )}
              <DMDiv>
                <FieldLabel required>
                  {isStaffResource || isFacultyResource
                    ? 'Teaching Since'
                    : 'Experience'}
                </FieldLabel>
                <DMInput
                  name="experience"
                  value={formik.values.experience}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={
                    isStaffResource || isFacultyResource
                      ? 'Enter Teaching Since'
                      : 'Enter Experience'
                  }
                  className={inputClassName}
                />
                <FieldError formik={formik} name="experience" />
              </DMDiv>
              {isFacultyResource && (
                <>
                  <DMDiv>
                    <FieldLabel required>Gender</FieldLabel>
                    <DMSelect
                      name="gender"
                      value={formik.values.gender}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={inputClassName}
                    >
                      <DMOption value="">Select Gender</DMOption>
                      <DMOption value="Male">Male</DMOption>
                      <DMOption value="Female">Female</DMOption>
                      <DMOption value="Other">Other</DMOption>
                    </DMSelect>
                    <FieldError formik={formik} name="gender" />
                  </DMDiv>
                  <EditWebsiteContentMobileField
                    formik={formik}
                    names={{
                      gender: 'gender',
                      countryCode: 'countryCode',
                      mobile: 'mobile',
                    }}
                    inputClassName={inputClassName}
                    labelClassName={labelClassName}
                    errorClassName={errorClassName}
                    className="col-span-1"
                    countryCodeClassName="w-[5.8vw]"
                  />
                </>
              )}
              {isFacultyResource && (
                <DMDiv>
                  <FieldLabel required>Email-Id</FieldLabel>
                  <DMInput
                    type="email"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="example@email.com"
                    className={inputClassName}
                  />
                  <FieldError formik={formik} name="email" />
                </DMDiv>
              )}
              {config.api.resource === 'staff' && (
                <>
                  <DMDiv>
                    <FieldLabel required>Preferred Language</FieldLabel>
                    <DMSelect
                      name="languageId"
                      value={formik.values.languageId || ''}
                      onChange={event =>
                        formik.setFieldValue(
                          'languageId',
                          Number(event.target.value)
                        )
                      }
                      onBlur={formik.handleBlur}
                      className={inputClassName}
                    >
                      <DMOption value="">Select Language</DMOption>
                      {languageList.map(language => (
                        <DMOption
                          key={language.LANGUAGE_ID}
                          value={language.LANGUAGE_ID}
                        >
                          {language.NAME}
                        </DMOption>
                      ))}
                    </DMSelect>
                    <FieldError formik={formik} name="languageId" />
                  </DMDiv>
                  <DMDiv>
                    <FieldLabel required>City</FieldLabel>
                    <DMSelect
                      name="cityId"
                      value={formik.values.cityId || ''}
                      onChange={event =>
                        formik.setFieldValue(
                          'cityId',
                          Number(event.target.value)
                        )
                      }
                      onBlur={formik.handleBlur}
                      className={inputClassName}
                    >
                      <DMOption value="">Select City</DMOption>
                      {cityList.map(city => (
                        <DMOption key={city.CITY_ID} value={city.CITY_ID}>
                          {city.NAME}
                        </DMOption>
                      ))}
                    </DMSelect>
                    <FieldError formik={formik} name="cityId" />
                  </DMDiv>
                  <DMDiv>
                    <FieldLabel required>Birth Date</FieldLabel>
                    <DMInput
                      type="date"
                      name="birthDate"
                      value={formik.values.birthDate}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={inputClassName}
                    />
                    <FieldError formik={formik} name="birthDate" />
                  </DMDiv>
                  <DMDiv>
                    <FieldLabel required>Joining Date</FieldLabel>
                    <DMInput
                      type="date"
                      name="joiningDate"
                      value={formik.values.joiningDate}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={inputClassName}
                    />
                    <FieldError formik={formik} name="joiningDate" />
                  </DMDiv>
                  <DMDiv>
                    <FieldLabel>Classes Taught</FieldLabel>
                    <DMSelect
                      name="classTeacherIds"
                      value={formik.values.classTeacherIds[0] || ''}
                      onChange={event => {
                        const selectedClassId = Number(event.target.value);
                        formik.setFieldValue(
                          'classTeacherIds',
                          selectedClassId ? [selectedClassId] : []
                        );
                      }}
                      onBlur={formik.handleBlur}
                      className={inputClassName}
                    >
                      <DMOption value="">Select Class</DMOption>
                      {classList.map(classItem => (
                        <DMOption
                          key={classItem.CLASS_ID}
                          value={classItem.CLASS_ID}
                        >
                          {classItem.NAME}
                        </DMOption>
                      ))}
                    </DMSelect>
                    <FieldError formik={formik} name="classTeacherIds" />
                  </DMDiv>
                </>
              )}
            </DMDiv>

            <DMDiv
              className={`grid auto-rows-max ${
                isFacultyResource
                  ? 'col-span-2 grid-cols-[1fr_1fr] justify-self-end gap-x-[1.2vw] gap-y-[1.8vh] w-[34vw]'
                  : 'grid-cols-[1fr_0.45fr_0.5fr] gap-x-[1.5vw] gap-y-[1.2vh]'
              }`}
            >
              {!isFacultyResource && (
                <DMDiv className="col-span-3">
                  <FieldLabel required>Email-Id</FieldLabel>
                  <DMInput
                    type="email"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="example@email.com"
                    className={inputClassName}
                  />
                  <FieldError formik={formik} name="email" />
                </DMDiv>
              )}
              {!isFacultyResource && (
                <>
                  <EditWebsiteContentGenderField
                    formik={formik}
                    names={{
                      gender: 'gender',
                      countryCode: 'countryCode',
                      mobile: 'mobile',
                    }}
                    inputClassName={inputClassName}
                    labelClassName={labelClassName}
                    errorClassName={errorClassName}
                  />
                  <EditWebsiteContentMobileField
                    formik={formik}
                    names={{
                      gender: 'gender',
                      countryCode: 'countryCode',
                      mobile: 'mobile',
                    }}
                    inputClassName={inputClassName}
                    labelClassName={labelClassName}
                    errorClassName={errorClassName}
                  />
                </>
              )}
              {config.api.resource === 'staff' && (
                <>
                  <DMDiv className="col-span-3 grid grid-cols-2 gap-x-[1.2vw] gap-y-[0.9vh]">
                    <DMDiv>
                      <FieldLabel required>Pin Code</FieldLabel>
                      <DMInput
                        name="pinCode"
                        value={formik.values.pinCode}
                        onChange={event =>
                          formik.setFieldValue(
                            'pinCode',
                            event.target.value.replace(/\D/g, '')
                          )
                        }
                        onBlur={formik.handleBlur}
                        placeholder="Enter Pin Code"
                        inputMode="numeric"
                        className={inputClassName}
                      />
                      <FieldError formik={formik} name="pinCode" />
                    </DMDiv>
                    <DMDiv>
                      <FieldLabel>Aadhaar Number</FieldLabel>
                      <DMInput
                        name="aadhaarNumber"
                        value={formik.values.aadhaarNumber}
                        onChange={event =>
                          formik.setFieldValue(
                            'aadhaarNumber',
                            event.target.value.replace(/\D/g, '')
                          )
                        }
                        onBlur={formik.handleBlur}
                        placeholder="Enter Aadhaar Number"
                        inputMode="numeric"
                        className={inputClassName}
                      />
                      <FieldError formik={formik} name="aadhaarNumber" />
                    </DMDiv>
                  </DMDiv>
                  <DMDiv className="col-span-3">
                    <FieldLabel required>Address</FieldLabel>
                    <DMTextarea
                      name="address"
                      value={formik.values.address}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Enter Address"
                      className={`${inputClassName} h-[13.5vh] resize-none py-[1.1vh]`}
                    />
                    <FieldError formik={formik} name="address" />
                  </DMDiv>
                </>
              )}
              <DMFormActions
                className="col-start-1 col-span-full mt-[1vh] justify-start"
                isSubmitting={formik.isSubmitting}
                isDisabled={isUploadingImage || isEnhancingImage || isGeneratingDescription}
                showReset={!isEditMode}
                onReset={() => formik.resetForm()}
                submitLabel={isEditMode ? 'Save Changes' : 'Upload Profile'}
                submitClassName="h-[6.2vh] rounded-full bg-primary-main px-[2vw] text-[1.85vh] font-semibold text-white disabled:opacity-60"
                resetClassName="h-[6.2vh] rounded-full border border-gray-300 bg-white px-[2vw] text-[1.85vh] font-semibold text-gray-950"
              />
            </DMDiv>
          </Form>
          <ImagePreviewModal
            imageUrl={previewImageUrl}
            alt={formik.values.facultyName || 'Profile'}
            onClose={() => setPreviewImageUrl('')}
          />
        </DMDiv>
      )}
    </Formik>
  );
}
